import { Employee, Role } from '../types/models';
import { premiumPosPath, toLegacyPosPath } from '../features/premium-pos/paths';
import { getModuleFlags, isModuleEnabledForPath } from './moduleAccess';

/** Prefixes only ADMIN may access (non-admin users are denied). Longest match checked first. */
export const DEFAULT_ADMIN_ONLY_PREFIXES: string[] = [
  '/dashboard',
  '/purchase-stocks-list',
  '/employees',
  '/deliveries',
  '/bundles',
  '/sellers',
  '/samples',
  '/routes',
  '/admin',
  '/settings',
  '/audit',
  '/zeettu-card-audit',
].sort((a, b) => b.length - a.length);

/** Prefixes only SUPER_ADMIN may access. Checked before the admin bypass in canAccessPath. */
export const SUPER_ADMIN_ONLY_PREFIXES: string[] = ['/database-config'].sort(
  (a, b) => b.length - a.length,
);

/** Exact paths only SUPER_ADMIN may access; sub-paths keep their normal rules (e.g. `/stock-items/:stockId`). */
export const SUPER_ADMIN_ONLY_EXACT_PATHS: string[] = ['/stock-items'];

export type RouteAccessRule = { prefix: string; allowed: Role[] };

/**
 * Longest-prefix-first rules. More specific paths must appear before their parents
 * (e.g. `/pos/stock-adjustments` before `/pos`).
 */
export const DEFAULT_ALLOW_RULES: RouteAccessRule[] = [
  {
    prefix: '/pos/stock-adjustments',
    allowed: [Role.ADMIN, Role.OFFICE, Role.CASHIER, Role.STORE_MANAGER, Role.OUTLET_MANAGER],
  },
  { prefix: '/pos/outlets', allowed: [Role.ADMIN, Role.OFFICE] },
  { prefix: '/pos/terminals', allowed: [Role.ADMIN, Role.OFFICE] },
  { prefix: '/pos/shifts', allowed: [Role.ADMIN, Role.OFFICE, Role.STORE_MANAGER, Role.OUTLET_MANAGER] },
  { prefix: '/pos/staff', allowed: [Role.ADMIN, Role.OUTLET_MANAGER] },
  { prefix: '/pos/roster', allowed: [Role.ADMIN, Role.OUTLET_MANAGER] },
  {
    prefix: '/thermal-printer',
    allowed: [
      Role.ADMIN,
      Role.OFFICE,
      Role.CASHIER,
      Role.OUTLET_MANAGER,
      Role.STORE_MANAGER,
      Role.Z_REP,
      Role.I_REP,
      Role.RECOVERY_OFFICER,
      Role.PROCUREMENT,
      Role.WAREHOUSE,
    ],
  },
  { prefix: '/pos', allowed: [Role.ADMIN, Role.OFFICE, Role.CASHIER, Role.OUTLET_MANAGER] },
  { prefix: '/brands', allowed: [Role.ADMIN, Role.OFFICE] },
  {
    prefix: '/customers',
    allowed: [Role.ADMIN, Role.OFFICE, Role.Z_REP, Role.I_REP, Role.RECOVERY_OFFICER],
  },
  { prefix: '/delivery-packages', allowed: [Role.ADMIN, Role.OFFICE, Role.Z_REP] },
  { prefix: '/enquiries', allowed: [Role.ADMIN, Role.OFFICE] },
  { prefix: '/service-tickets', allowed: [Role.ADMIN, Role.OFFICE] },
  { prefix: '/source-documents', allowed: [Role.ADMIN, Role.OFFICE] },
  { prefix: '/items', allowed: [Role.ADMIN, Role.OFFICE, Role.PROCUREMENT] },
  /** Core stock record (stockId) — admin/office only. */
  { prefix: '/stock-items', allowed: [Role.ADMIN] },
  {
    prefix: '/payments',
    allowed: [
      Role.ADMIN,
      Role.OFFICE,
      Role.Z_REP,
      Role.I_REP,
      Role.RECOVERY_OFFICER,
      Role.CASHIER,
    ],
  },
  {
    prefix: '/grn',
    allowed: [
      Role.ADMIN,
      Role.OFFICE,
      Role.Z_REP,
      Role.I_REP,
      Role.RECOVERY_OFFICER,
      Role.PROCUREMENT,
      Role.STORE_MANAGER,
      Role.CASHIER,
      Role.DRIVER,
      Role.WAREHOUSE,
      Role.ACCOUNTANT,
    ],
  },
  {
    prefix: '/return-to-supplier',
    allowed: [
      Role.ADMIN,
      Role.OFFICE,
      Role.Z_REP,
      Role.I_REP,
      Role.RECOVERY_OFFICER,
      Role.PROCUREMENT,
      Role.STORE_MANAGER,
      Role.CASHIER,
      Role.DRIVER,
      Role.WAREHOUSE,
      Role.ACCOUNTANT,
    ],
  },
  {
    prefix: '/replacement-note',
    allowed: [
      Role.ADMIN,
      Role.OFFICE,
      Role.Z_REP,
      Role.I_REP,
      Role.RECOVERY_OFFICER,
      Role.PROCUREMENT,
      Role.STORE_MANAGER,
      Role.CASHIER,
      Role.DRIVER,
      Role.WAREHOUSE,
      Role.ACCOUNTANT,
    ],
  },
  {
    prefix: '/service-receipt',
    allowed: [
      Role.ADMIN,
      Role.OFFICE,
      Role.Z_REP,
      Role.I_REP,
      Role.RECOVERY_OFFICER,
      Role.PROCUREMENT,
      Role.STORE_MANAGER,
      Role.CASHIER,
      Role.DRIVER,
      Role.WAREHOUSE,
      Role.ACCOUNTANT,
    ],
  },
  { prefix: '/remuneration', allowed: [Role.ADMIN, Role.ACCOUNTANT] },
  { prefix: '/expenses', allowed: [Role.ADMIN, Role.ACCOUNTANT] },
  {
    prefix: '/reports/pdr',
    allowed: [Role.ADMIN, Role.OFFICE, Role.I_REP, Role.RECOVERY_OFFICER],
  },
  {
    prefix: '/reports/irr',
    allowed: [Role.ADMIN, Role.OFFICE, Role.I_REP, Role.RECOVERY_OFFICER],
  },
  {
    prefix: '/reports/recovery-dashboard',
    allowed: [Role.ADMIN, Role.OFFICE, Role.ACCOUNTANT, Role.RECOVERY_OFFICER, Role.I_REP],
  },
  {
    prefix: '/reports/str',
    allowed: [Role.ADMIN, Role.OFFICE, Role.I_REP, Role.STORE_MANAGER, Role.WAREHOUSE],
  },
  { prefix: '/purchase-reports', allowed: [Role.ADMIN, Role.OFFICE, Role.PROCUREMENT] },
  /** Purchase stock hub: admin sees stock report UI; procurement sees purchase reports list (see PurchaseStockPage). */
  { prefix: '/purchase-stock', allowed: [Role.ADMIN, Role.PROCUREMENT] },
  { prefix: '/reports/zdr', allowed: [Role.ADMIN, Role.OFFICE, Role.Z_REP] },
  { prefix: '/zeettu', allowed: [Role.ADMIN, Role.OFFICE, Role.Z_REP] },
  {
    prefix: '/installment-cards',
    allowed: [Role.ADMIN, Role.OFFICE, Role.I_REP, Role.RECOVERY_OFFICER],
  },
  {
    prefix: '/trip',
    allowed: [Role.ADMIN, Role.Z_REP, Role.I_REP, Role.RECOVERY_OFFICER, Role.DRIVER],
  },
  { prefix: '/vehicles', allowed: [Role.ADMIN, Role.DRIVER] },
  { prefix: '/stores', allowed: [Role.ADMIN, Role.STORE_MANAGER, Role.WAREHOUSE] },
  /** Store receiving / inventory (features/stock/store-stock). */
  { prefix: '/store-stock', allowed: [Role.ADMIN, Role.STORE_MANAGER] },
  /** Field rep trip/dispatch stock UX (legacy REP); admins use the full report view. */
  { prefix: '/trip-stock', allowed: [Role.ADMIN, Role.Z_REP, Role.I_REP] },
].sort((a, b) => b.prefix.length - a.prefix.length);

/** First accessible path wins; used after login and for `/` when not admin. */
const POST_LOGIN_REDIRECT_CANDIDATES: string[] = [
  '/grn',
  '/return-to-supplier',
  '/replacement-note',
  '/service-receipt',
  '/remuneration',
  '/expenses',
  '/stores',
  '/store-stock',
  '/pos/sales',
  '/premium-pos/sales',
  '/pos/stock-adjustments',
  '/premium-pos/stock-adjustments',
  '/vehicles',
  '/trip',
  '/customers',
  '/items',
  '/purchase-reports',
  '/payments',
  '/brands',
  '/enquiries',
  '/service-tickets',
  '/delivery-packages',
  '/reports/zdr',
  '/reports/pdr',
  '/reports/irr',
  '/reports/recovery-dashboard',
  '/reports/str',
  '/zeettu',
  '/installment-cards',
  '/trip-stock',
  '/purchase-stock',
];

let runtimeAllowRules: RouteAccessRule[] | null = null;
let runtimeAdminOnlyPrefixes: string[] | null = null;

/**
 * Company settings replace the default matrix. Fill in prefixes that were added
 * later in code (e.g. `/source-documents`) so office is not locked out of new modules.
 * Product default roles for `/source-documents` always include office.
 */
export function mergeAllowRulesWithDefaults(rules: RouteAccessRule[]): RouteAccessRule[] {
  // Settings saved before the outlet manager role existed get its default POS access; once any rule names it,
  // the company's choices stand.
  const knowsOutletManager = rules.some((r) => r.allowed.includes(Role.OUTLET_MANAGER));
  const defaults = new Map(DEFAULT_ALLOW_RULES.map((d) => [d.prefix, d.allowed]));
  const merged = rules.map((r) => {
    const allowed = [...r.allowed] as Role[];
    if (!knowsOutletManager && defaults.get(r.prefix)?.includes(Role.OUTLET_MANAGER)) allowed.push(Role.OUTLET_MANAGER);
    return { prefix: r.prefix, allowed };
  });
  const seen = new Set(merged.map((r) => r.prefix));
  for (const def of DEFAULT_ALLOW_RULES) {
    if (!seen.has(def.prefix)) {
      merged.push({ prefix: def.prefix, allowed: [...def.allowed] });
    }
  }
  return merged
    .map((r) => {
      if (r.prefix !== '/source-documents') return r;
      const allowed = new Set(r.allowed);
      allowed.add(Role.ADMIN);
      allowed.add(Role.OFFICE);
      return { prefix: r.prefix, allowed: [...allowed] };
    })
    .sort((a, b) => b.prefix.length - a.prefix.length);
}

export function setRouteAccessConfig(
  rules: RouteAccessRule[] | null,
  adminOnlyPrefixes: string[] | null,
): void {
  runtimeAllowRules = rules == null ? null : mergeAllowRulesWithDefaults(rules);
  // Never treat source-documents as admin-only — office must retain access.
  runtimeAdminOnlyPrefixes =
    adminOnlyPrefixes == null
      ? null
      : adminOnlyPrefixes.filter((p) => p !== '/source-documents');
}

export function getRouteAccessConfig(): {
  allowRules: RouteAccessRule[];
  adminOnlyPrefixes: string[];
} {
  return {
    allowRules: runtimeAllowRules ?? DEFAULT_ALLOW_RULES,
    adminOnlyPrefixes: runtimeAdminOnlyPrefixes ?? DEFAULT_ADMIN_ONLY_PREFIXES,
  };
}

function normalizeRole(value: string): Role {
  return String(value).trim().toLowerCase().replace(/\s+/g, '_') as Role;
}

export function getEffectiveRoles(user: Employee | null): Role[] {
  if (!user) return [];
  const fromList =
    user.roles && user.roles.length > 0 ? user.roles : user.role ? [user.role] : [];
  return [...new Set(fromList.map((r) => normalizeRole(String(r))))];
}

export function isSuperAdmin(roles: Role[]): boolean {
  return roles.includes(Role.SUPER_ADMIN);
}

/** Admin-equivalent route/data bypass: admin or super_admin. */
export function isAdmin(roles: Role[]): boolean {
  return roles.includes(Role.ADMIN) || roles.includes(Role.SUPER_ADMIN);
}

function pathMatchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function isAdminOnlyPath(pathname: string): boolean {
  const { adminOnlyPrefixes } = getRouteAccessConfig();
  return adminOnlyPrefixes.some((p) => pathMatchesPrefix(pathname, p));
}

function isSuperAdminOnlyPath(pathname: string): boolean {
  const exactPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return (
    SUPER_ADMIN_ONLY_EXACT_PATHS.includes(exactPath) ||
    SUPER_ADMIN_ONLY_PREFIXES.some((p) => pathMatchesPrefix(pathname, p))
  );
}

/**
 * Whether the user should see the simplified rep trip/dispatch stock view (not the full report).
 */
export function shouldShowRepFieldStockView(roles: Role[]): boolean {
  if (isAdmin(roles)) return false;
  return roles.includes(Role.Z_REP) || roles.includes(Role.I_REP);
}

/**
 * Route access for authenticated users. Does not handle /login or /register.
 */
export function canAccessPath(pathname: string, roles: Role[]): boolean {
  const path = pathname.split('?')[0] || '/';

  if (!isModuleEnabledForPath(path)) {
    return false;
  }

  if (isSuperAdminOnlyPath(path)) {
    return isSuperAdmin(roles);
  }

  if (isAdmin(roles)) {
    return true;
  }

  // Premium POS pages reuse the company's `/pos` permission rules.
  const rulePath = toLegacyPosPath(path);
  const { allowRules } = getRouteAccessConfig();
  for (const rule of allowRules) {
    if (pathMatchesPrefix(rulePath, rule.prefix)) {
      return rule.allowed.some((r) => roles.includes(r));
    }
  }

  if (isAdminOnlyPath(rulePath)) {
    return false;
  }

  return false;
}

/** Premium POS pages in fallback order for unknown `/premium-pos/*` URLs. */
const PREMIUM_POS_FALLBACKS = ['sales', 'sales/new', 'stock-adjustments', 'shifts', 'staff', 'roster', 'outlets', 'terminals'];

/** First Premium POS page these roles may open, or null when none is allowed. */
export function firstAccessiblePosPath(roles: Role[]): string | null {
  for (const sub of PREMIUM_POS_FALLBACKS) {
    const path = premiumPosPath(sub);
    if (canAccessPath(path, roles)) return path;
  }
  return null;
}

/** Cashiers and outlet managers land straight on the Premium POS checkout while Premium POS is enabled. */
const CASHIER_PREMIUM_POS_LANDING = premiumPosPath('sales/new');

export function getPostLoginRedirectPath(roles: Role[]): string {
  if (isAdmin(roles)) {
    return '/dashboard';
  }
  if (
    (roles.includes(Role.CASHIER) || roles.includes(Role.OUTLET_MANAGER)) &&
    getModuleFlags().isPOSEnabled &&
    canAccessPath(CASHIER_PREMIUM_POS_LANDING, roles)
  ) {
    return CASHIER_PREMIUM_POS_LANDING;
  }
  for (const path of POST_LOGIN_REDIRECT_CANDIDATES) {
    if (isModuleEnabledForPath(path) && canAccessPath(path, roles)) {
      return path;
    }
  }
  return '/customers';
}
